import { useState } from 'react'
import { useCreateUser } from '@/hooks/useUsers'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { AlertTriangle } from 'lucide-react'
import type { MemberUser } from '@/types/admin'

export function CreateUserDialog() {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [emailWarning, setEmailWarning] = useState<string | null>(null)
  const createUser = useCreateUser()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setEmailWarning(null) // Clear any previous warnings

    createUser.mutate(
      { email, name },
      {
        onSuccess: (data: MemberUser) => {
          // Check if email failed to send
          if (data.email_sent === false && data.email_error) {
            setEmailWarning(data.email_error)
            // Don't close dialog - let admin see the warning
          } else {
            // Success - close dialog and reset
            setEmail('')
            setName('')
            setEmailWarning(null)
            setOpen(false)
          }
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Create Member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Member</DialogTitle>
          <DialogDescription>
            Create a new member account in the member portal. A temporary password will be generated
            and sent to their email.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {createUser.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to create member.{' '}
                {createUser.error instanceof Error ? createUser.error.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          {emailWarning && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Member created successfully, but email failed:</strong>
                <div className="mt-1 text-sm">{emailWarning}</div>
                <div className="mt-2 text-sm">
                  You can resend the welcome email from the members table (look for members in
                  FORCE_CHANGE_PASSWORD status).
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="member@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false)
                setEmailWarning(null)
              }}
            >
              {emailWarning ? 'Close' : 'Cancel'}
            </Button>
            {!emailWarning && (
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending ? 'Creating...' : 'Create Member'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
